// Everything the app does, in one place: the Features page, the header menu, the footer
// and the waitlist interests all read from here. `how` is the steps a person takes,
// `notes` the details worth knowing. Keep every word to what the app actually ships
// (see backend/docs/*.md for each feature).
import {
    ArrowLeftRight,
    AudioLines,
    ScrollText,
    CalendarClock,
    CreditCard,
    Fingerprint,
    Gamepad2,
    Gift,
    Goal,
    Languages,
    LineChart,
    MessageSquareText,
    PiggyBank,
    Receipt,
    ScanLine,
    ShoppingBag,
    Smartphone,
    Sparkles,
    TrainFront,
    UsersRound,
    WalletCards,
} from "lucide-react";

export const FEATURE_GROUPS = [
    {
        id: "save",
        label: "Save",
        title: "Save for the thing, not for “someday”",
        intro: "Goals turn a vague plan into a number, a date and a daily habit.",
    },
    {
        id: "track",
        label: "Track",
        title: "Every spend, sorted on its own",
        intro: "Bank alerts become a clean, categorised record, with insights that tell you what changed.",
    },
    {
        id: "pay",
        label: "Pay",
        title: "Recharges, bills and transfers in one place",
        intro: "Pay from your Accruewise Wallet and keep every receipt next to your spends.",
    },
    {
        id: "card",
        label: "Wallet & card",
        title: "A wallet and a RuPay card that spend your savings",
        intro: "When a goal is ready, the money is too: online, in store and on the metro.",
    },
    {
        id: "rewards",
        label: "Rewards",
        title: "Small wins for good habits",
        intro: "Scratch cards, gift vouchers and games that make saving a little more fun.",
    },
    {
        id: "assistant",
        label: "Assistant",
        title: "Ask your money a question",
        intro: "Type or talk to the Accruewise Assistant in English or Hindi. It asks before it acts.",
    },
    {
        id: "security",
        label: "Security",
        title: "Locked down by default",
        intro: "Your MPIN or fingerprint guards the app and every payment.",
    },
];

export const FEATURES = [
    // ---- Save
    {
        id: "goals",
        group: "save",
        Icon: Goal,
        title: "Savings goals",
        short: "Set a goal for a phone, a trip or an emergency fund and see exactly how far you are.",
        points: ["Target amount and date", "A daily or weekly plan to get there", "Progress you can see at a glance"],
        how: ["Pick a category, like travel, shopping, gadgets or sports kit, or make your own", "Set the amount and the date you want it by", "Money comes in from round-ups, auto-save and top-ups, and the goal fills up"],
        notes: ["You get an update at 25%, 50% and 75%, and when the goal is reached"],
    },
    {
        id: "autosave",
        group: "save",
        Icon: PiggyBank,
        title: "Round-ups & auto-save",
        short: "Spare change from everyday payments and auto-save rules fill your goals in the background.",
        how: ["Pay through Accruewise", "The payment is rounded up and the spare change goes to your goal", "It adds up in the background, one payment at a time"],
        notes: ["For example: a ₹187 coffee rounds up to ₹200, and ₹13 goes to the goal"],
    },
    {
        id: "marketplace",
        group: "save",
        Icon: ShoppingBag,
        title: "Goals for real products",
        short: "Pick a product in the Marketplace and save toward its actual price.",
        to: "/marketplace",
        how: ["Browse products in the Marketplace", "Add one to a goal, and its price becomes the target", "Save toward it the same way as any other goal"],
        notes: ["Buying inside the app isn't open yet; you spend the saved money with your wallet or card"],
    },

    // ---- Track
    {
        id: "spends",
        group: "track",
        Icon: MessageSquareText,
        title: "Automatic spend tracking",
        short: "On Android, bank and UPI alerts turn into entries by themselves. Reminders and duplicates are skipped.",
        points: ["Only bank alerts leave your phone", "Autopay and bill reminders are ignored", "Deleted entries stay deleted"],
        badge: "New",
        how: ["Allow SMS access once", "Accruewise reads alerts from bank, card and UPI sender IDs", "Each payment is filed with its amount, payee, category and time"],
        notes: ["Android only", "The first sync looks back 90 days", "Reminders, failed payments, OTPs, offers and repeat alerts are skipped", "A deleted entry doesn't come back on the next sync"],
    },
    {
        id: "paste-sms",
        group: "track",
        Icon: ScanLine,
        title: "Paste a bank SMS",
        short: "On iPhone, copy an alert and paste it: amount, payee, category and date fill in for you.",
        badge: "New",
        how: ["Copy a bank alert from Messages", "Tap Paste bank SMS on the Add screen", "Check the amount, payee, category and date, then save"],
        notes: ["Works on Android too", "You can also add entries by hand, with tags, notes and a photo of the bill"],
    },
    {
        id: "insights",
        group: "track",
        Icon: LineChart,
        title: "Insights that explain",
        short: "This month against last, where the money went, your busiest day and your recurring payments.",
        badge: "New",
        how: ["Open Insights for any month or range", "Read the tips first: what changed and why", "Then your pace, categories, top payees, busiest day and recurring payments"],
        notes: ["Compared with the previous period", "Recurring payments are spotted from the last 120 days"],
    },
    {
        id: "budgets",
        group: "track",
        Icon: CalendarClock,
        title: "Budgets with a daily limit",
        short: "Set a monthly budget and see how much is safe to spend each day to stay within it.",
        how: ["Set a monthly amount, or pick your own dates", "See what's safe to spend each day", "Get a warning when your pace would take you over"],
        notes: ["A budget is optional: the tracker works without one", "Export any period as a spreadsheet (CSV)"],
    },

    // ---- Pay
    {
        id: "recharge",
        group: "pay",
        Icon: Smartphone,
        title: "Mobile & DTH recharge",
        short: "Browse plans by operator and circle and recharge in a few taps.",
        how: ["Enter the number; the operator and circle fill in once you've recharged it before", "Pick a plan: your last plan and the best value per day are suggested", "Approve with your MPIN or fingerprint"],
        notes: ["Mobile and DTH", "A failed recharge is refunded to your wallet on its own"],
    },
    {
        id: "bills",
        group: "pay",
        Icon: Receipt,
        title: "Bill payments (BBPS)",
        short: "Electricity, broadband, gas, water and more through Bharat BillPay, with reminders before the due date.",
        how: ["Pick a category and your biller", "Enter your consumer number and fetch the bill", "See the total, including any fee, and pay with your MPIN or fingerprint"],
        notes: ["Electricity, water, gas, broadband, postpaid, FASTag, loan EMIs, insurance and more", "An invoice for every successful payment", "Saved billers remind you before the next bill is due"],
    },
    {
        id: "transfers",
        group: "pay",
        Icon: ArrowLeftRight,
        title: "Pay to bank & UPI",
        short: "Send money to a bank account you've saved by IMPS, or pay a UPI ID, confirmed with an OTP.",
        how: ["Save the bank account once; this asks for your MPIN", "Pick it and enter an amount", "Confirm with the OTP sent to your mobile"],
        notes: ["Transfers go only to accounts you've saved", "The receipt updates its own status, and you can share it"],
    },
    {
        id: "ncmc",
        group: "pay",
        Icon: TrainFront,
        title: "NCMC transit top-up",
        short: "Top up your National Common Mobility Card for metro gates and buses from your wallet.",
        badge: "New",
        how: ["Open NCMC top-up", "Enter an amount", "Approve with your MPIN or fingerprint, and the card's balance updates"],
        notes: ["Tap the card at metro gates, buses and other NCMC readers"],
    },

    // ---- Wallet & card
    {
        id: "wallet",
        group: "card",
        Icon: WalletCards,
        title: "Accruewise Wallet",
        short: "Issued by our partner ToPay. A quick KYC gets you a wallet to add money, hold your savings and see every transaction in a passbook.",
        how: ["Complete a quick KYC in the app", "Your wallet gets its own account details and UPI ID", "Add money from any bank account or UPI app"],
        notes: ["Issued by our partner ToPay", "One balance for goals, bills, recharges and the card"],
    },
    {
        id: "card",
        group: "card",
        Icon: CreditCard,
        title: "Prepaid RuPay card",
        short: "A virtual RuPay card for online and in-store payments. Block it, reset its PIN or apply for a physical card in the app.",
        to: "/card",
        how: ["Activate your wallet, and the virtual card comes with it", "See the card details when you need them", "Pay online or in store wherever RuPay is accepted"],
        notes: ["Switch online, in-store, contactless, ATM and international use on or off", "Block the card or reset its PIN in the app", "Request a physical card when you want one"],
    },

    // ---- Rewards
    {
        id: "scratch",
        group: "rewards",
        Icon: Sparkles,
        title: "Scratch cards & coupons",
        short: "Milestones like finishing a goal unlock a scratch card, and some reveal a brand coupon.",
        how: ["Reach a milestone, like finishing a goal", "A scratch card lands in Coupons & Rewards", "Scratch it: some hide a brand coupon, some say better luck next time"],
        notes: ["Each coupon has its own terms and expiry"],
    },
    {
        id: "vouchers",
        group: "rewards",
        Icon: Gift,
        title: "Gift vouchers",
        short: "Buy gift vouchers from popular brands straight from your wallet, for yourself or to gift.",
        how: ["Pick a brand, a value and how many", "Pay from your wallet with your MPIN or fingerprint", "The voucher appears in My vouchers"],
        notes: ["If a voucher can't be issued, the money goes back to your wallet"],
    },
    {
        id: "games",
        group: "rewards",
        Icon: Gamepad2,
        title: "Games",
        short: "Quick games that earn Accruewise Coins, XP and badges. Coins have no cash value.",
        notes: ["Accruewise Coins have no cash value"],
    },
    {
        id: "refer",
        group: "rewards",
        Icon: UsersRound,
        title: "Invite friends",
        short: "Share Accruewise with friends and family in a tap, so they can start saving too.",
        how: ["Tap Invite friends", "Share your link on WhatsApp or anywhere else"],
    },

    // ---- Assistant
    {
        id: "assistant",
        group: "assistant",
        Icon: AudioLines,
        title: "Accruewise Assistant",
        short: "“How much did I spend on food this month?” or “Save ₹18,000 for Goa by March”. Type or speak, in English or Hindi.",
        points: ["Every change shows a confirmation card", "Payments wait for your MPIN or fingerprint", "Bank transfers open ready to confirm, never sent from chat"],
        to: "/ai-agents",
        how: ["Type, or tap the mic and talk, in English or Hindi", "It answers from your own goals, spends and wallet", "Anything that changes shows a confirmation card first"],
        notes: ["Payments wait for your MPIN or fingerprint, with a daily limit", "Bank transfers open the payment screen filled in; you confirm with an OTP", "It won't recommend other apps, stocks or loans"],
    },

    // ---- Security
    {
        id: "security",
        group: "security",
        Icon: Fingerprint,
        title: "MPIN & biometric lock",
        short: "A 6-digit MPIN, or Face ID or fingerprint, unlocks the app and confirms every payment.",
        notes: ["Easy PINs like 123456 are refused", "It locks after two minutes in the background", "Five wrong tries lock it for 15 minutes"],
    },
    {
        id: "languages",
        group: "security",
        Icon: Languages,
        title: "English & हिंदी",
        short: "Use the whole app in English or Hindi, and switch any time from your profile.",
        notes: ["The Assistant understands both"],
    },
    {
        id: "privacy",
        group: "security",
        Icon: ScrollText,
        title: "Clear policies",
        short: "Plain-language privacy, refund and grievance policies, and account deletion when you ask for it.",
        notes: ["Delete your account from Profile → About → Delete account"],
    },
];

export const featuresIn = (group) => FEATURES.filter((f) => f.group === group);

// Options for "What are you most excited about?" on the waitlist form. Values must match
// the backend's allowed interests.
export const WAITLIST_INTERESTS = [
    { value: "goals", label: "Savings goals" },
    { value: "spends", label: "Spend tracker" },
    { value: "payments", label: "Bills & recharges" },
    { value: "card", label: "RuPay card" },
    { value: "rewards", label: "Rewards" },
    { value: "assistant", label: "AI assistant" },
    { value: "vouchers", label: "Gift vouchers" },
    { value: "ncmc", label: "NCMC top-up" },
];
